import type Geometry from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import type Point from 'ol/geom/Point';
import type { AppStore } from '../core/store';
import type { AppState, MapTool, SelectionEntry, VectorFeatureRecord, WorkingGeometryType } from '../core/types';
import { geodesicLength } from '../domain/measurement';
import { formatPlanningArea, formatPlanningLength, isPlanningLayer, planningMeasures, planningPreset, updatePlanningGeometry } from '../domain/planningObjects';
import { replaceLayer } from '../domain/workingLayers';
import type { PlanningGeometryEditor } from '../map/PlanningGeometryEditor';
import { planningEditStyle } from '../map/planningStyle';
import { icon } from './icons';

const esc = (value: string): string => value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);

type Editor = Pick<PlanningGeometryEditor, 'start' | 'stop' | 'geometry' | 'undo' | 'setDeleteMode' | 'canUndo' | 'deletingVertices'>;

export interface PlanningEditHooks {
  /** Hides the object on its layer while the preview draws it (null restores it). */
  readonly setEditTarget: (target: { readonly layerId: string; readonly featureId: string } | null) => void;
  /** Where the outcome is reported once the toolbar closes. */
  readonly status: (text: string) => void;
  /** Reopens Properties after applying (mobile closes the sheet while editing). */
  readonly showProperties?: () => void;
}

interface Session {
  readonly layerId: string;
  readonly featureId: string;
  readonly geometryType: WorkingGeometryType;
  readonly label: string;
  /** The record's Feature/geometry/revision when the edit started: anything else means the object changed meanwhile. */
  readonly feature: VectorFeatureRecord['feature'];
  readonly geometry: Geometry;
  readonly revision: number;
  readonly previousTool: MapTool;
}

const ACTION: Readonly<Record<WorkingGeometryType, { readonly title: string; readonly help: string }>> = {
  Point: { title: 'Recolocando', help: 'Arrastra el punto o toca su nueva ubicación' },
  LineString: { title: 'Editando recorrido', help: 'Arrastra un vértice para moverlo; arrastra un tramo para añadir uno' },
  Polygon: { title: 'Editando vértices', help: 'Arrastra un vértice para moverlo; arrastra un lado para añadir uno' },
};

/**
 * «Editar recorrido / vértices / Recolocar» of ONE planning object: the edit runs on a clone (PlanningGeometryEditor),
 * the toolbar shows the live measure, and only «Aplicar» writes the project — after checking the object is still the
 * one that was opened (not deleted, replaced by an undo, reopened project or locked layer). «Cancelar» leaves it intact.
 * The Maestro, its GeometryEditHistory and every non-planning layer are out of reach of this flow.
 */
export class PlanningEditController {
  private session: Session | null = null;

  constructor(private readonly element: HTMLElement, private readonly store: AppStore, private readonly editor: Editor, private readonly hooks: PlanningEditHooks) {
    element.addEventListener('click', (event) => {
      const button = (event.target as Element).closest<HTMLButtonElement>('[data-planning-edit-action]');
      if (!button || button.disabled) return;
      const action = button.dataset.planningEditAction;
      if (action === 'apply') this.apply();
      else if (action === 'cancel') this.cancel();
      else if (action === 'undo') { this.editor.undo(); this.renderLive(); }
      else if (action === 'delete-mode') { this.editor.setDeleteMode(!this.editor.deletingVertices); this.render(); }
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && this.session) this.cancel(); });
    store.subscribe((state, previous) => this.onState(state, previous));
  }

  get isEditing(): boolean { return this.session !== null; }
  /** V1.9.0 E5: the global Deshacer (Ctrl+Z, toolbar) reaches this editor's own undo while it is open. */
  get canUndoStep(): boolean { return Boolean(this.session) && this.editor.canUndo; }
  undoStep(): void { if (!this.canUndoStep) return; this.editor.undo(); this.renderLive(); }

  /** Opens the edit; false (with the reason in the status) when the object is not an editable planning object. */
  start(layerId: string, featureId: string): boolean {
    if (this.session) this.cancel('');
    const state = this.store.getState(); const layer = state.layers.find(({ id }) => id === layerId);
    const record = layer?.featuresById.get(featureId); const geometry = record?.feature.getGeometry(); const meta = record?.projectMetadata?.planning;
    if (!isPlanningLayer(layer) || !record || !geometry || !meta) { this.hooks.status('Solo se editan objetos de capas de planificación.'); return false; }
    if (layer.locked) { this.hooks.status(`${layer.name} está bloqueada: desbloquéala para editar.`); return false; }
    if (state.geometryEditSession) { this.hooks.status('Termina primero la edición de geometría del Maestro.'); return false; }
    const geometryType = record.projectMetadata!.geometryType;
    const preset = planningPreset(meta.preset, geometryType);
    // Map clicks must not change the selection while editing: the tool goes first (it resets map interactions).
    const previousTool = state.activeTool === 'navigate' ? 'select' : state.activeTool;
    this.store.update({ activeTool: 'navigate' }, { markDirty: false });
    this.session = { layerId, featureId, geometryType, label: String(record.attributes.Nombre || preset.label), feature: record.feature, geometry, revision: geometry.getRevision(), previousTool };
    this.editor.start(geometry, planningEditStyle(record), () => this.renderLive());
    this.hooks.setEditTarget({ layerId, featureId });
    if (window.matchMedia?.('(max-width: 760px)').matches) document.querySelector<HTMLElement>('[data-mobile-close]')?.click();
    this.render();
    return true;
  }

  /** Writes the edited geometry (measures, automatic label and exportable attributes follow) and closes the edit. */
  apply(): void {
    const session = this.session; if (!session) return;
    const state = this.store.getState(); const stale = this.staleReason(state);
    if (stale) { this.cancel(`Edición cancelada: ${stale}. No se aplicó ninguna geometría.`); return; }
    const geometry = this.editor.geometry(); const layer = state.layers.find(({ id }) => id === session.layerId)!;
    if (!geometry) { this.cancel(); return; }
    let updated;
    try { updated = updatePlanningGeometry(layer, session.featureId, geometry); }
    catch (error) { this.setNote(error instanceof Error ? error.message : 'La geometría editada no es válida.'); return; }
    this.close();
    const entry: SelectionEntry = { source: 'generic', layerId: layer.id, kind: 'feature', id: session.featureId };
    this.store.update({ layers: replaceLayer(state.layers, updated), activeLayerId: layer.id, activeTool: session.previousTool, selectionSet: { layerId: layer.id, entries: [entry], focus: entry }, genericSelection: { layerId: layer.id, featureId: session.featureId }, selection: null });
    this.hooks.status(`${session.label} actualizado · ${this.measureText(updated.featuresById.get(session.featureId)?.feature.getGeometry(), session)}.`);
    this.hooks.showProperties?.();
  }

  /** Discards the edit: the object keeps the geometry it had. */
  cancel(message = 'Edición cancelada; el objeto no cambió.'): void {
    const session = this.session; if (!session) return;
    this.close();
    if (this.store.getState().activeTool === 'navigate') this.store.update({ activeTool: session.previousTool }, { markDirty: false });
    if (message) this.hooks.status(message);
  }

  render(): void {
    const session = this.session;
    this.element.hidden = !session;
    if (!session) { this.element.innerHTML = ''; return; }
    const action = ACTION[session.geometryType]; const vertices = session.geometryType !== 'Point';
    this.element.innerHTML = `<span class="edit-badge" aria-hidden="true"></span><span class="edit-context"><strong>${action.title} · ${esc(session.label)}</strong><small data-planning-edit-note>${action.help}${vertices ? (this.editor.deletingVertices ? ' · toca un vértice para borrarlo' : ' · Alt+clic borra un vértice') : ''}</small></span>
      <span class="planning-edit-measure" data-planning-edit-measure></span>
      <span class="edit-sep"></span>
      ${vertices ? `<button type="button" data-planning-edit-action="delete-mode" aria-pressed="${this.editor.deletingVertices}" title="Toca o haz clic en un vértice para borrarlo (se conservan al menos ${session.geometryType === 'Polygon' ? 'tres' : 'dos'})">${icon('erase')}Borrar vértices</button>` : ''}
      <button type="button" class="icon-only" data-planning-edit-action="undo" title="Deshacer el último cambio" aria-label="Deshacer">${icon('undo')}</button>
      <span class="edit-sep"></span>
      <button type="button" data-planning-edit-action="cancel">Cancelar</button>
      <button type="button" class="apply" data-planning-edit-action="apply">${icon('check')}Aplicar</button>`;
    this.renderLive();
  }

  /** Only the measure and Undo change while dragging: no rebuild of the toolbar per pointer move. */
  private renderLive(): void {
    const session = this.session; if (!session) return;
    const measure = this.element.querySelector('[data-planning-edit-measure]');
    if (measure) {
      const [now, before] = this.measureText(this.editor.geometry() ?? undefined, session, true).split(' (antes ');
      measure.innerHTML = `${esc(now ?? '')}${before ? ` <small>(antes ${esc(before)}</small>` : ''}`;
    }
    const undo = this.element.querySelector<HTMLButtonElement>('[data-planning-edit-action="undo"]'); if (undo) undo.disabled = !this.editor.canUndo;
  }

  private measureText(geometry: Geometry | undefined, session: Session, compare = false): string {
    if (!geometry) return '';
    if (session.geometryType === 'Point') {
      const from = (session.geometry as Point).getCoordinates(); const to = (geometry as Point).getCoordinates();
      return `Desplazamiento ${formatPlanningLength(geodesicLength(new LineString([from, to])))}`;
    }
    const now = planningMeasures(geometry); const before = planningMeasures(session.geometry);
    if (now.lengthM !== null) return `Longitud ${formatPlanningLength(now.lengthM)}${compare ? ` (antes ${formatPlanningLength(before.lengthM ?? 0)})` : ''}`;
    if (now.areaM2 !== null) return `Área ${formatPlanningArea(now.areaM2)}${compare ? ` (antes ${formatPlanningArea(before.areaM2 ?? 0)})` : ''}`;
    return '';
  }

  private setNote(text: string): void {
    const note = this.element.querySelector('[data-planning-edit-note]'); if (note) note.textContent = text;
    this.hooks.status(text);
  }

  /** Why the edit can no longer be applied (null while the object is still the one that was opened). */
  private staleReason(state: AppState): string | null {
    const session = this.session; if (!session) return null;
    const layer = state.layers.find(({ id }) => id === session.layerId);
    if (!isPlanningLayer(layer)) return 'la capa de planificación ya no existe';
    if (layer.locked) return 'la capa se bloqueó';
    const record = layer.featuresById.get(session.featureId);
    if (!record) return 'el objeto fue eliminado';
    const geometry = record.feature.getGeometry();
    if (record.feature !== session.feature || geometry !== session.geometry || geometry?.getRevision() !== session.revision) return 'el objeto cambió fuera de esta edición';
    return null;
  }

  private onState(state: AppState, previous: AppState): void {
    if (!this.session) return;
    const stale = this.staleReason(state);
    if (stale) { this.cancel(`Edición cancelada: ${stale}. No se aplicó ninguna geometría.`); return; }
    // Another tool or the Maestro geometry editor took over the map: the planning edit gives way without applying.
    if ((state.activeTool !== 'navigate' && state.activeTool !== previous.activeTool) || (state.geometryEditSession && !previous.geometryEditSession)) this.cancel('Edición de geometría cancelada; el objeto no cambió.');
  }

  private close(): void {
    this.session = null;
    this.editor.stop();
    this.hooks.setEditTarget(null);
    this.render();
  }
}
